import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { SCORED_QUESTIONS, TOTAL_QUESTIONS } from './scoring';
import { useScore } from './ScoreContext';
import { createScoreResultUrl } from './scoreResultUrl';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isComplete,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const firstUnansweredIndex = useMemo(() => {
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    return index === -1 ? TOTAL_QUESTIONS - 1 : index;
  }, [answers]);
  useEffect(() => {
    if (isDiagnosticOpen) {
      setCurrentIndex(firstUnansweredIndex);
    }
  }, [firstUnansweredIndex, isDiagnosticOpen]);
  if (!isDiagnosticOpen) {
    return null;
  }
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const isLastQuestion = !canGoForward;
  const selectAnswer = (score: number) => {
    setAnswer(question.id, score);
    if (canGoForward) {
      window.setTimeout(() => setCurrentIndex(index => index + 1), 180);
    }
  };
  const showScore = () => {
    if (!isComplete || !result) {
      const missingIndex = SCORED_QUESTIONS.findIndex(item => answers[item.id] === undefined);
      setCurrentIndex(missingIndex === -1 ? TOTAL_QUESTIONS - 1 : missingIndex);
      return;
    }
    window.open(createScoreResultUrl(result), '_blank', 'noopener,noreferrer');
  };
  return <div className="fixed inset-0 z-[60] overflow-y-auto bg-paper px-6 py-6 md:py-10" role="dialog" aria-modal="true" aria-labelledby="diagnostic-title" data-id="element-86" data-mp="2mcimsj707rbu">
      
      <div className="mx-auto max-w-3xl" data-id="element-87" data-mp="w0l1cshooizo">
        <header className="flex items-center justify-between gap-4" data-id="element-88" data-mp="ryhfd9xe1w05">
          <div data-id="element-89" data-mp="s0aocvvh4sbb">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-90" data-mp="s0u5499e1anr">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-91" data-mp="2w3dkp8g7e1l1">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" onClick={closeDiagnostic} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close diagnostic" data-id="element-92" data-mp="360i7nahvi12w">
            
            <X className="h-5 w-5" data-id="element-93" data-mp="3ezvh435zypnm" />
          </button>
        </header>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-94" data-mp="3jrn09smtpt3q">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-95" data-mp="3orj4nszfnmjt" />)}
        </div>

        <main className="py-12 md:py-16" data-id="element-96" data-mp="3tnzt6oajo2xf">
          <p className="text-sm font-medium text-text-muted" data-id="element-97" data-mp="1ls8jml8mtp6">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h1 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-98" data-mp="1m5vyqvl9d5ze">
            
            {question.prompt}
          </h1>

          <div className="mt-9 flex flex-col gap-3" data-id="element-99" data-mp="1d4peqcjehd1e">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-100" data-mp="25s204jdtret4">
                  
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass text-white' : 'border-gray-300 text-navy'}`} data-id="element-101" data-mp="2ojl1ymw44mtk">
                    
                    {isSelected ? <Check className="h-4 w-4" data-id="element-102" data-mp="28bezcnqbt9nu" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-103" data-mp="1ubvrzx5ca2cl">
                    {option.text}
                  </span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-104" data-mp="1zd2x1og3knfk">
            <button type="button" onClick={() => setCurrentIndex(index => index - 1)} disabled={!canGoBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-105" data-mp="2n1vw3ts0nbee">
              
              <ArrowLeft className="h-4 w-4" data-id="element-106" data-mp="2i8jjyj4iwx2j" />
              Back
            </button>

            {isLastQuestion ? <button type="button" onClick={showScore} disabled={selectedScore === undefined || !isComplete} className="inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-sand px-6 py-3 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus:ring-2 focus:ring-seaglass disabled:cursor-not-allowed disabled:opacity-40" data-id="element-107" data-mp="35xufnm70o19t">
              
                See my score
                <ArrowRight className="h-4 w-4" data-id="element-108" data-mp="182yqntajkhch" />
              </button> : <button type="button" onClick={() => setCurrentIndex(index => index + 1)} disabled={selectedScore === undefined} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-109" data-mp="141d6dmvoh0s9">
              
                Next question
                <ArrowRight className="h-4 w-4" data-id="element-110" data-mp="pea7pcvmcm3c" />
              </button>}
          </div>
        </main>
      </div>
    </div>;
}